import { WordGrammarPositions, SentenceTypes } from '../data/enums';
import { LEVEL_MASTERY_THRESHOLD } from '../data/constants';

/**
 * calculates the word offset (x axis) relative to the game container in the sentence area.
 * @param {number} x - the current word position on the x axis.
 * @param {number} y - the current word position on the y axis.
 * @param {object} sentenceZonePosition - contains information about the area where the sentence is created: width, height and position (x, y, left, top).
 * @return {number}
 */
export const calculateOrder = (x, y, sentenceZonePosition) => {
  let order = x;
  if (y > sentenceZonePosition.y + sentenceZonePosition.height * 0.4) {
    order += sentenceZonePosition.width * 1.5;
  }
  if (y > sentenceZonePosition.y + sentenceZonePosition.height) {
    // guarantees that clicking a word at the bottom of the screen will add it to the end of the second line, as opposed to somewhere in the middle.
    order += sentenceZonePosition.width;
  }
  return order;
};

/**
 * Predicate deciding whether the primary sentence is correctly created.
 * @param {array} order - information about the current word order;
 * @return {boolean}
 */
export const isValidPrimarySentence = (order) => {
  for (let i = 0; i < order.length; i++) {
    if (order[i].indexInTrackerProps !== i) {
      return false;
    }
  }
  return true;
};

/**
 * Predicate deciding whether any secondary sentence is correctly created.
 * @param {string} userResponse - sentence created by user;
 * @param {array} targetSentences - array of possible correct sentences;
 * @return {boolean}
 */
export const isValidSecondarySentence = (userResponse, targetSentences) => {
  const [, ...secondarySentences] = targetSentences;
  return secondarySentences.some(({ sentence }) => sentence === userResponse);
};

/**
 * update the grammatical state for the first and last words in a sentence.
 * @param {array} trackerData - information about the current state of each word;
 * @param {string} userResponse - sentence created by user;
 * @param {array} order - information about the current word order;
 * @param {array} targetSentences - possible correct sentences;
 * @return {array} updated trackerData
 */
export const updateGrammarState = (trackerData, userResponse, order, targetSentences) => {
  const trackerProps = [...trackerData];
  const [sentenceData] = targetSentences.filter(({ sentence }) => sentence === userResponse) || [];

  const firstWordKey = order[0].indexInTrackerProps;
  const lastWordKey = order[order.length - 1].indexInTrackerProps;

  trackerProps[firstWordKey] = {
    ...trackerProps[firstWordKey],
    grammarState: WordGrammarPositions.FirstWord,
  };
  trackerProps[lastWordKey] = {
    ...trackerProps[lastWordKey],
    grammarState: WordGrammarPositions.LastWord,
    punctuation: sentenceData?.type === SentenceTypes.question ? '?' : '.',
  };

  return trackerProps;
};

/**
 * return a sentence created by user.
 * @param {array} order - information about the current word order;
 * @return {string}
 */
export const getUserResponse = (order = []) => order.map(({ text }) => text).join(' ');

/**
 * a predicate that determines whether the user receives the mastery criteria in a round.
 *
 * @param {object} questionCount - number of questions in a round.
 * @param {object} correctAnswerCount - the number of questions the user answered correctly.
 * @return {boolean}
 */
export const isLevelMastered = (questionCount, correctAnswerCount = 0) => {
  if (!questionCount) {
    return false;
  }
  return correctAnswerCount / questionCount >= LEVEL_MASTERY_THRESHOLD;
};

const hasAccents = (str) => {
  const normalized = str.normalize('NFD');
  const accentPattern = /[\u0300-\u036f]/g;
  return accentPattern.test(normalized);
};

const removeAccents = (str) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/**
 * return a voice id that will be used to fetch an audio file for the passed word.
 * @param {string} word - a string to create the id out of;
 * @return {string}
 */
export const generateVoiceId = (word) => {
  if (typeof word !== 'string' || !word?.length) {
    return null;
  }

  const formattedWord = word.replace(/['-.,?!]|_/g, '').toLowerCase();
  const voiceId = hasAccents(formattedWord) ? `${removeAccents(formattedWord)}2` : formattedWord;

  return voiceId;
};
